import { NoStyleButton } from "@fullscript/aviary-web";
import { faChevronLeft } from "@fullscript/pro-regular-svg-icons";
import { useTranslation } from "react-i18next";

import { useSharedGlobalConfig } from "@shared/hooks/useSharedGlobalConfig/useSharedGlobalConfig";
import { FontAwesomeIcon } from "@shared/react-fontawesome/react-fontawesome";
import { useLocation, useNavigate } from "@shared/react-router-dom/react-router-dom";
import { authRoutes } from "@unauthenticated/shared/data/authRoutes";
import { l } from "@unauthenticated/signupSignin/locales/i18n";

import { noStyleButton, chevron } from "./ReturnToSignInButton.styles";

/** Path segments for welcome forgot-password, derived from authRoutes so the parser stays in sync. */
const WELCOME_FORGOT_PASSWORD_PATH = authRoutes
  .welcomeForgotPassword("_", "_")
  .split("/")
  .filter(Boolean);
const WELCOME_SEGMENT = WELCOME_FORGOT_PASSWORD_PATH[0];
const WELCOME_FORGOT_PASSWORD_END =
  WELCOME_FORGOT_PASSWORD_PATH[WELCOME_FORGOT_PASSWORD_PATH.length - 1];

/**
 * Parse welcome forgot-password pathname to get storeSlug and optional identitySlug.
 * Uses segments derived from authRoutes.welcomeForgotPassword so the route remains the single source of truth.
 * Valid shapes: [welcome, storeSlug, forgot-password] or [welcome, storeSlug, identitySlug, forgot-password].
 */
const getWelcomeForgotPasswordParams = (pathname: string): [string, string] | null => {
  if (typeof pathname !== "string" || !pathname) return null;

  const segments = pathname.split("/").filter(Boolean);
  const isWelcomeForgotPassword =
    segments[0] === WELCOME_SEGMENT &&
    segments[segments.length - 1] === WELCOME_FORGOT_PASSWORD_END &&
    (segments.length === 3 || segments.length === 4);

  if (!isWelcomeForgotPassword) return null;

  return segments.length === 3 ? [segments[1], ""] : [segments[1], segments[2]];
};

const ReturnToSignInButton = ({ ...rest }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname, search } = useLocation();
  const { isEmerson } = useSharedGlobalConfig();

  const handleGoBack = () => {
    const welcomeParams = getWelcomeForgotPasswordParams(pathname);
    if (welcomeParams) {
      const [storeSlug, identitySlug] = welcomeParams;
      navigate(`${authRoutes.welcomeStart(storeSlug, identitySlug)}${search ?? ""}`);

      return;
    }
    if (isEmerson) {
      navigate(authRoutes.emerson.login);
    } else {
      navigate(authRoutes.login);
    }
  };

  return (
    <NoStyleButton type="button" onClick={handleGoBack} customCss={noStyleButton} {...rest}>
      <FontAwesomeIcon className="icon-chevron" icon={faChevronLeft} size="sm" css={chevron} />
      {t(l.signIn.ReturnToSignIn)}
    </NoStyleButton>
  );
};

export { ReturnToSignInButton };
